import axios from 'axios';
import { getApiUrl, isDevelopment } from './environment';

// Get API URL dynamically based on environment
const API_URL = getApiUrl();

// Log API URL in development for debugging
if (isDevelopment) {
  console.log('🌐 API Base URL:', API_URL);
}

const api = axios.create({
  baseURL: API_URL,
  timeout: 30000, // 30 second timeout for better reliability (default)
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create a separate instance for long-running requests (like story generation)
export const apiLongTimeout = axios.create({
  baseURL: API_URL,
  timeout: 120000, // 2 minutes timeout for story generation and other long operations
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token interceptor for long timeout instance
apiLongTimeout.interceptors.request.use(
  (config) => {
    const isAdminRoute = config.url?.includes('/admin/');
    const token = isAdminRoute 
      ? localStorage.getItem('admin_token')
      : localStorage.getItem('token');
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle token expiration for long timeout instance
apiLongTimeout.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const errorMessage = error.response?.data?.message;
    // Don't logout for subscription limit errors (requiresUpgrade)
    const requiresUpgrade = error.response?.data?.requiresUpgrade;
    
    // Check if account is inactive (ACCOUNT_INACTIVE message)
    if (status === 403 && errorMessage === 'ACCOUNT_INACTIVE') {
      const url = error.config?.url || '';
      const isAdminRoute = url.includes('/admin/');
      const isUserAuthRoute =
        url.includes('/auth/login') || url.includes('/auth/register') || url.includes('/auth/google');

      // Handle inactive user account - logout and redirect to login
      if (!isAdminRoute && !isUserAuthRoute) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        // Store reason for logout so Login page can display message
        sessionStorage.setItem('logoutReason', 'ACCOUNT_INACTIVE');
        sessionStorage.setItem('logoutMessage', error.response?.data?.error || 'Your account has been deactivated. Please contact support.');
        // Redirect to login page
        if (typeof window !== 'undefined' && window.location) {
          window.location.href = '/login?reason=inactive';
        }
        return Promise.reject(error);
      }
    }
    
    if (status === 401 || (status === 403 && !requiresUpgrade && errorMessage !== 'ACCOUNT_INACTIVE')) {
      const url = error.config?.url || '';
      const isAdminRoute = url.includes('/admin/');
      const isAdminAuthRoute = url.includes('/admin/login');
      const isUserAuthRoute =
        url.includes('/auth/login') || url.includes('/auth/register');

      if (isAdminRoute && !isAdminAuthRoute) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin');
        window.location.href = '/admin/login';
        return Promise.reject(error);
      }

      if (!isAdminRoute && !isUserAuthRoute) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

// Add token to requests
api.interceptors.request.use(
  (config) => {
    // Check if it's an admin route
    const isAdminRoute = config.url?.includes('/admin/');
    const token = isAdminRoute 
      ? localStorage.getItem('admin_token')
      : localStorage.getItem('token');
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle token expiration and account inactive status
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const errorMessage = error.response?.data?.message;
    // Don't logout for subscription limit errors (requiresUpgrade)
    const requiresUpgrade = error.response?.data?.requiresUpgrade;
    
    // Check if account is inactive (ACCOUNT_INACTIVE message)
    if (status === 403 && errorMessage === 'ACCOUNT_INACTIVE') {
      const url = error.config?.url || '';
      const isAdminRoute = url.includes('/admin/');
      const isUserAuthRoute =
        url.includes('/auth/login') || url.includes('/auth/register') || url.includes('/auth/google');

      // Handle inactive user account - logout and redirect to login
      if (!isAdminRoute && !isUserAuthRoute) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        // Store reason for logout so Login page can display message
        sessionStorage.setItem('logoutReason', 'ACCOUNT_INACTIVE');
        sessionStorage.setItem('logoutMessage', error.response?.data?.error || 'Your account has been deactivated. Please contact support.');
        // Redirect to login page
        if (typeof window !== 'undefined' && window.location) {
          window.location.href = '/login?reason=inactive';
        }
        return Promise.reject(error);
      }
    }
    
    if (status === 401 || (status === 403 && !requiresUpgrade && errorMessage !== 'ACCOUNT_INACTIVE')) {
      const url = error.config?.url || '';
      const isAdminRoute = url.includes('/admin/');
      const isAdminAuthRoute = url.includes('/admin/login');
      const isUserAuthRoute =
        url.includes('/auth/login') || url.includes('/auth/register');

      // Don't redirect when user/admin is trying to log in or register
      if (isAdminRoute && !isAdminAuthRoute) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin');
        window.location.href = '/admin/login';
        return Promise.reject(error);
      }

      if (!isAdminRoute && !isUserAuthRoute) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export default api;

